import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, ChevronDown, Eye, Gift, Users, Sparkles, Heart, Trophy, Crown, Zap, Star, Globe2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { UserAvatar, RankBadge } from '../components/RankBadge';
import VisitorsDialog from '../components/VisitorsDialog';
import { motion, AnimatePresence } from 'framer-motion';
import AdBanner from '../components/AdBanner';
import VipDialog from '../components/VipDialog';
import SpinWheel from '../components/SpinWheel';
import VipBadge from '../components/VipBadge';
import { attractiveOnlineCount, attractivePlanetCount } from '../lib/socialProof';
import TravelerGlossary from '../components/TravelerGlossary';
import { toast } from 'sonner';

// Curated cartoon dating profiles for the "Find a match" teaser card.
// Self-hosted from /public/dating/* — previously these were 12 external CDN
// PNGs (~2 MB each) that failed to load on production and rendered as broken
// image icons. Locally-served JPEGs total ~450 KB across all 12.
const MATCH_PROFILES = [
  '/dating/w1.jpg',
  '/dating/w2.jpg',
  '/dating/w3.jpg',
  '/dating/w4.jpg',
  '/dating/m1.jpg',
  '/dating/m2.jpg',
  '/dating/m3.jpg',
  '/dating/m4.jpg',
  '/dating/r1.jpg',
  '/dating/r2.jpg',
  '/dating/r3.jpg',
  '/dating/r4.jpg',
];

const DAILY_FALLBACK = [
  { id: 'visit_planets',  title: 'Visit your Planets',     reward: 50  },
  { id: 'send_gift',      title: 'Send 1 gift',            reward: 100 },
  { id: 'chat_3_rooms',   title: 'Chat in 3 rooms',        reward: 75  },
  { id: 'take_daily_spin',title: 'Take your daily spin',   reward: 60  },
  { id: 'play_minigame',  title: 'Play a mini-game',       reward: 120 },
];

export default function HomePage() {
  const { user, refresh, bumpStats } = useAuth();
  const [online, setOnline] = useState([]);
  const [planets, setPlanets] = useState([]);
  const [visitors, setVisitors] = useState({ count: 0, recent: [] });
  const [visitorsOpen, setVisitorsOpen] = useState(false);
  const [vipOpen, setVipOpen] = useState(false);
  const [spinOpen, setSpinOpen] = useState(false);
  const [daily, setDaily] = useState({ tasks: DAILY_FALLBACK.map(t => ({ ...t, done: false, claimed: false, count: 0 })), completed: 0, total: 5, bonus: 250, bonus_claimed: false, seconds_left: 0 });

  const loadDaily = async () => {
    try { const { data } = await api.get('/tasks/daily'); setDaily(data); }
    catch (_e) { /* endpoint may be down briefly — daily card just stays empty */ }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [oRes, pRes] = await Promise.all([api.get('/users/online'), api.get('/planets')]);
        setOnline(oRes.data); setPlanets(pRes.data);
      } catch (_e) { /* transient — the next 60s tick will retry */ }
    };
    load();
    loadDaily();
    // Refresh the online list every 60s so as users close their tab and age
    // out of the 5-minute presence window, the count updates on-screen.
    const refreshOnline = async () => {
      try { const { data } = await api.get('/users/online'); setOnline(data); } catch (_) { /* silent */ }
    };
    const iv = setInterval(refreshOnline, 60_000);
    return () => clearInterval(iv);
  }, []);

  // Load real visitor count for the home card
  useEffect(() => {
    let alive = true;
    api.get('/users/me/visitors')
      .then(r => { if (alive) setVisitors(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const claim = async (taskId) => {
    try {
      const { data } = await api.post('/tasks/claim', { task_id: taskId });
      const xp = data.xp_added ? ` · +${data.xp_added.toLocaleString()} XP` : '';
      toast.success(`+${data.stars_added.toLocaleString()} ⭐${xp} claimed!`);
      // Optimistic bump so header chips update instantly
      bumpStats({ stars: data.stars_added || 0, xp: data.xp_added || 0 });
      await refresh();
      await loadDaily();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not claim');
    }
  };

  const tasksDone = daily.completed;
  const secondsLeft = daily.seconds_left || 0;
  const hoursLeft = Math.floor(secondsLeft / 3600);
  const minutesLeft = Math.floor((secondsLeft % 3600) / 60);

  // Collapse secondary widgets (Visitors, Voyagers, VIP rewards) behind a
  // single toggle so the Home first-render stays focused on today's actions
  // (World Quest, Find a Match, Daily Tasks). Persisted so power-users who
  // like the full dashboard don't have to expand it every session.
  const [moreOpen, setMoreOpen] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('wv_home_more_open') === '1',
  );
  const toggleMore = () => {
    setMoreOpen(prev => {
      const next = !prev;
      try { localStorage.setItem('wv_home_more_open', next ? '1' : '0'); } catch (_e) { /* storage disabled — the toggle just won't persist */ }
      return next;
    });
  };

  // Attractive "travelers online" number for the greeting + voyagers card.
  // Grows with real online activity, waves gently through the day.
  const displayedOnline = attractiveOnlineCount(online.length);

  return (
    <div className="space-y-5">
      {/* Welcome */}
      <div>
        <h1 className="font-display font-bold text-2xl md:text-3xl">Welcome back, {user?.displayName?.split(' ')[0]} <span className="text-amber-300">{user?.flag}</span></h1>
        <p className="text-white/60 text-sm mt-1" data-testid="home-online-count-greeting">{displayedOnline.toLocaleString()} travelers online · ready to vibe?</p>
      </div>

      {/* Traveler Glossary — plain-English intro to WorldVibe vocab (dismissible) */}
      <TravelerGlossary />

      {/* World Quest — daily global ritual */}
      <WorldQuestCard />

      {/* Find a match — 18+ only */}
      {user?.is_adult && <FindAMatchCard />}

      {/* Daily tasks — kept above the fold: today's earn hook */}

      {/* Daily tasks */}
      <div>
        <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Daily tasks</div>
        <Card className="bg-gradient-to-br from-fuchsia-500/15 to-purple-600/15 border-fuchsia-500/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="text-4xl">🎁</div>
              <div className="flex-1">
                <div className="font-semibold">Complete 5 tasks and earn a reward</div>
                <div className="text-xs text-white/60 mt-1">
                  {tasksDone}/{daily.total} · {hoursLeft}h {minutesLeft}m left
                </div>
                <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-fuchsia-400 to-amber-300" style={{ width: `${(tasksDone / Math.max(1, daily.total)) * 100}%` }} />
                </div>
              </div>
              {tasksDone === daily.total && !daily.bonus_claimed && (
                <Button
                  size="sm"
                  onClick={() => claim('_bonus')}
                  data-testid="claim-daily-bonus"
                  className="bg-gradient-to-r from-amber-400 to-orange-500 text-black border-0 rounded-full font-semibold"
                >
                  <Star size={12} className="mr-1" fill="currentColor" /> Claim +{daily.bonus}
                </Button>
              )}
            </div>
            <div className="mt-4 space-y-1.5">
              {daily.tasks.map(t => (
                <button
                  key={t.id}
                  type="button"
                  disabled={!t.done || t.claimed}
                  onClick={() => claim(t.id)}
                  data-testid={`daily-task-${t.id}`}
                  className={`w-full text-left flex items-center gap-3 p-2 rounded-lg transition ${
                    t.claimed ? 'bg-emerald-500/10 cursor-default'
                      : t.done ? 'bg-fuchsia-500/10 hover:bg-fuchsia-500/20 cursor-pointer ring-1 ring-fuchsia-400/30'
                      : 'bg-white/5 cursor-default opacity-90'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    t.claimed ? 'bg-emerald-400 text-black'
                      : t.done ? 'bg-fuchsia-400 text-black animate-pulse'
                      : 'border border-white/20 text-white/40'
                  }`}>{t.claimed ? '✓' : t.done ? '!' : ''}</div>
                  <span className={`text-sm flex-1 ${t.claimed ? 'text-white/60 line-through' : ''}`}>
                    {t.title}
                    {t.threshold > 1 && !t.claimed && (
                      <span className="ml-2 text-[10px] text-white/40">({t.count}/{t.threshold})</span>
                    )}
                  </span>
                  <span className={`text-xs flex items-center gap-1.5 ${t.claimed ? 'text-emerald-300' : 'text-white/70'}`}>
                    {t.claimed ? (
                      <span className="text-emerald-300">claimed</span>
                    ) : (
                      <>
                        <span className="flex items-center gap-0.5 text-amber-300"><Star size={10} fill="currentColor" />+{t.reward}</span>
                        {t.xp ? <span className="flex items-center gap-0.5 text-cyan-300"><Zap size={10} fill="currentColor" />+{t.xp}</span> : null}
                      </>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Collapsible "More on WorldVibe" — hides Visitors + Voyagers +
          VIP rewards under a single toggle so the Home first-render stays
          focused on today's real actions (World Quest, Find a Match,
          Daily Tasks). Choice is persisted in localStorage. */}
      <button
        type="button"
        onClick={toggleMore}
        aria-expanded={moreOpen}
        data-testid="home-more-toggle"
        className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-white/15 transition"
      >
        <span className="flex items-center gap-2 text-sm text-white/85 font-medium">
          <Sparkles size={14} className="text-cyan-300" /> {moreOpen ? 'Hide' : 'Show'} visitors, voyagers &amp; VIP rewards
        </span>
        <ChevronDown
          size={16}
          className={`text-white/60 transition-transform ${moreOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {moreOpen && (
        <div className="space-y-5" data-testid="home-more-section">
          {/* Visitors */}
          <button
            type="button"
            onClick={() => setVisitorsOpen(true)}
            data-testid="home-visitors-card"
            className="w-full text-left"
          >
            <Card className="bg-[#161622] border-white/10 overflow-hidden hover:border-white/20 transition">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="font-display font-bold text-4xl text-amber-300" data-testid="home-visitors-count">{visitors.count}</div>
                <div className="flex-1">
                  <div className="font-medium">{visitors.count === 1 ? 'visitor in your Info' : 'visitors in your Info'}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {visitors.count > 0
                      ? `Recent profile visits${visitors.recent[0]?.displayName ? ` — latest: ${visitors.recent[0].displayName}` : ''}`
                      : "Nobody's peeked yet — keep chatting to get on the radar."}
                  </div>
                </div>
                <Eye className="text-cyan-400/40" size={36} />
                <ChevronRight className="text-white/40" size={18} />
              </CardContent>
            </Card>
          </button>

          {/* Voyagers Online */}
          <Card className="bg-[#161622] border-white/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-white/40">Cosmic Voyagers</div>
                <Link to="/app/messages" data-testid="home-friends-see-all" className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1">See all <ChevronRight size={12} /></Link>
              </div>
              <div className="mt-3 flex items-center gap-4">
                <div className="font-display font-bold text-3xl text-cyan-300" data-testid="home-friends-count">{displayedOnline.toLocaleString()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">orbiting now</div>
                  <div className="text-xs text-white/50 truncate" data-testid="home-friends-names">
                    {online.length === 0 ? 'Voyagers are drifting across every timezone right now' : (
                      <>
                        {online.slice(0, 3).map(u => u.displayName).join(', ')}
                        {online.length > 3 ? `, +${(displayedOnline - 3).toLocaleString()} more` : ` +${(displayedOnline - Math.min(3, online.length)).toLocaleString()} across the galaxy`}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex -space-x-2">
                  {online.slice(0, 5).map((u, i) => {
                    const dot = u.is_online ? (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 block w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#161622]"
                        data-testid={`home-online-dot-${u.user_id}`}
                        title="Online now"
                      />
                    ) : null;
                    const inner = (
                      <div className="relative">
                        <UserAvatar user={u} size={36} ring />
                        {dot}
                      </div>
                    );
                    return u.user_id ? (
                      <Link
                        key={u.user_id}
                        to={`/app/users/${u.user_id}`}
                        title={`View ${u.displayName}'s profile`}
                        data-testid={`home-online-${u.user_id}`}
                        className="hover:z-10 hover:scale-110 transition-transform"
                      >
                        {inner}
                      </Link>
                    ) : (
                      <div key={i}>{inner}</div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

      {/* VIP Card */}
      <div>
        <div className="text-xs uppercase tracking-wider text-white/40 mb-2">VIP Card</div>
        <Card className="bg-[#161622] border-white/10">
          <CardContent className="p-5">
            <div className="grid grid-cols-3 gap-3">
              {[
                { name: 'Wheel of Fortune', emoji: '🎡', cta: 'Spin',    color: 'from-rose-500 to-amber-500',
                  cb: () => setSpinOpen(true) },
                { name: 'Free Chest',       emoji: '💰', cta: 'Open',    color: 'from-emerald-500 to-teal-600',
                  cb: async () => {
                    try {
                      const { data } = await api.post('/vip/claim-weekly-gift');
                      toast.success(`Weekly gift ${data.emoji} +${data.stars_added} ⭐`);
                      refresh?.();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Weekly gift not ready yet.');
                    }
                  } },
                { name: 'Free Card',        emoji: '🃏', cta: 'Take it', color: 'from-pink-500 to-orange-500',
                  cb: () => {
                    const cards = [
                      { face: '💎', label: 'Diamond', stars: 200 },
                      { face: '🌟', label: 'Star',    stars: 150 },
                      { face: '🍀', label: 'Clover',  stars: 100 },
                      { face: '🔥', label: 'Flame',   stars: 250 },
                      { face: '👑', label: 'Crown',   stars: 500 },
                    ];
                    const pick = cards[Math.floor(Math.random() * cards.length)];
                    toast.success(`Free Card: ${pick.face} ${pick.label} +${pick.stars} ⭐`);
                    bumpStats?.({ stars: pick.stars });
                  } },
              ].map(c => {
                const locked = !user?.is_vip;
                return (
                  <button
                    key={c.name}
                    onClick={() => locked ? setVipOpen(true) : c.cb()}
                    data-testid={`vip-widget-${c.name.replace(/\s+/g,'-').toLowerCase()}`}
                    data-locked={locked}
                    className={`relative rounded-2xl p-4 border text-center transition ${locked ? 'bg-black/25 border-white/6 hover:border-amber-400/50 hover:bg-black/40' : 'bg-white/3 border-white/8 hover:bg-white/8 hover:-translate-y-0.5'}`}
                    title={locked ? 'VIP required — tap to unlock' : ''}
                  >
                    <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${c.color} flex items-center justify-center text-2xl shadow-lg ${locked ? 'opacity-50 grayscale' : ''}`}>{c.emoji}</div>
                    <div className={`text-xs font-medium mt-2 ${locked ? 'text-white/60' : ''}`}>{c.name}</div>
                    <div className={`mt-2 text-[11px] px-3 py-1 rounded-full inline-flex items-center gap-1 ${locked ? 'bg-amber-400/15 text-amber-200 border border-amber-400/30' : 'bg-white/10'}`}>
                      {locked ? (<><Crown size={10} className="text-amber-300" /> VIP</>) : c.cta}
                    </div>
                    {locked && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center border border-amber-400/40">
                        <Crown size={10} className="text-amber-300" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 rounded-2xl overflow-hidden flex items-center gap-4 p-4" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.15), rgba(244,114,182,0.1))', border: '1px solid rgba(251,191,36,0.25)' }}>
              <div className="text-5xl">🎁</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold flex items-center gap-2">
                  {user?.is_vip ? 'You are a VIP member' : 'Get free VIP-Card'}
                  <VipBadge active={user?.is_vip} size="md" animated data-testid="home-vip-badge" />
                </div>
                <div className="text-xs text-white/60">{user?.is_vip ? '2 free spins daily · weekly gift · VIP badge everywhere' : 'Rewards and bonuses every day'}</div>
              </div>
              <Button
                onClick={() => setVipOpen(true)}
                data-testid="vip-get-now-btn"
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white border-0 font-semibold"
              >
                {user?.is_vip ? 'Manage VIP' : 'Get now'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
        </div>
      )}

      {/* Trending planets */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-white/40 flex items-center gap-1"><Globe2 size={12} /> Trending planets</div>
          <Link to="/app/planets" className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1">See all <ChevronRight size={12} /></Link>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {planets.slice(0, 4).map(p => (
            <Link to={`/app/planets/${p.id}`} key={p.id} className="group rounded-2xl overflow-hidden bg-[#161622] border border-white/10 hover:border-white/20">
              <div className={`aspect-[5/3] bg-gradient-to-br ${p.color} relative`}>
                <div className="absolute inset-0 flex items-center justify-center text-5xl">{p.emoji}</div>
                <div className="absolute top-2 left-2 text-[9px] uppercase tracking-wider bg-black/40 backdrop-blur px-2 py-0.5 rounded-full flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /> {attractivePlanetCount(p.online, p.id).toLocaleString()} online
                </div>
              </div>
              <div className="p-3">
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[11px] text-white/50 truncate">{p.description}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Sponsored footer banner (long/wide ad slot — swap creative in AdBanner.jsx) */}
      <AdBanner />

      <VisitorsDialog open={visitorsOpen} onOpenChange={setVisitorsOpen} />
      <VipDialog open={vipOpen} onOpenChange={setVipOpen} />
      <SpinWheel open={spinOpen} onOpenChange={setSpinOpen} />
    </div>
  );
}


// ===================== Find a Match — animated carousel =====================
// Shows a window of 3 cartoon dating profiles at a time with prev/next
// controls + a slow auto-advance. Each card has its own subtle float and
// rotation; swapping the window slides the next trio in from the right.
function FindAMatchCard() {
  const [offset, setOffset] = useState(0);
  const [direction, setDirection] = useState(1);
  const total = MATCH_PROFILES.length;

  // Auto-advance every 4s (pauses on hover via mouseenter/leave)
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setDirection(1);
      setOffset((o) => (o + 1) % total);
    }, 4000);
    return () => clearInterval(id);
  }, [paused, total]);

  const visible = [0, 1, 2].map((i) => MATCH_PROFILES[(offset + i) % total]);
  const next = () => { setDirection(1); setOffset((o) => (o + 1) % total); };
  const prev = () => { setDirection(-1); setOffset((o) => (o - 1 + total) % total); };

  return (
    <Card className="bg-gradient-to-br from-rose-500/20 via-pink-500/15 to-fuchsia-500/15 border-rose-500/20 overflow-hidden relative">
      <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full blur-3xl opacity-40" style={{ background: '#F472B6' }} />
      <CardContent
        className="relative p-6 text-center"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="text-xs uppercase tracking-wider text-rose-300 mb-2">Find a match</div>

        <div className="relative flex items-center justify-center my-4">
          {/* Prev arrow */}
          <button
            type="button"
            onClick={prev}
            data-testid="find-match-prev"
            aria-label="Previous profiles"
            className="absolute left-0 sm:left-2 z-10 w-9 h-9 rounded-full bg-black/40 hover:bg-rose-500/80 backdrop-blur border border-white/15 flex items-center justify-center text-white transition"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Sliding cards window */}
          <div className="overflow-hidden w-[280px] sm:w-[340px] h-28 sm:h-32 flex items-center justify-center">
            <AnimatePresence mode="wait" initial={false} custom={direction}>
              <motion.div
                key={offset}
                custom={direction}
                initial={{ x: direction * 80, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -direction * 80, opacity: 0 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
                className="flex items-center justify-center gap-3"
              >
                {visible.map((src, i) => (
                  <motion.div
                    key={`${offset}-${i}`}
                    data-testid={`find-match-card-${i}`}
                    initial={{ rotate: (i - 1) * 8, y: 0 }}
                    animate={{
                      rotate: [(i - 1) * 8, (i - 1) * 8 + 3, (i - 1) * 8],
                      y: [0, -4, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: i * 0.3,
                    }}
                    whileHover={{ scale: 1.08, rotate: (i - 1) * 8, zIndex: 5 }}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-2 border-rose-400/60 shadow-lg shadow-rose-500/20"
                  >
                    <img src={src} alt="Dating profile preview" loading="lazy" className="w-full h-full object-cover" />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Next arrow */}
          <button
            type="button"
            onClick={next}
            data-testid="find-match-next"
            aria-label="Next profiles"
            className="absolute right-0 sm:right-2 z-10 w-9 h-9 rounded-full bg-black/40 hover:bg-rose-500/80 backdrop-blur border border-white/15 flex items-center justify-center text-white transition"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Pager dots */}
        <div className="flex items-center justify-center gap-1.5 mb-2" data-testid="find-match-dots">
          {MATCH_PROFILES.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === offset ? 'w-5 bg-rose-400' : 'w-1.5 bg-white/25'}`}
            />
          ))}
        </div>

        <div className="text-sm font-medium">Fill in your profile to meet new people</div>
        <Link to="/app/profile">
          <Button className="mt-4 bg-rose-500 hover:bg-rose-600 text-white border-0 rounded-full px-7 font-semibold">
            Add profile
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}


/**
 * WorldQuestCard — cosmic hero on the Home page that surfaces today's
 * single global quest. Deep-links into the dedicated /app/quest screen
 * for the actual answering + world feed.
 *
 * States:
 *   - Loading: skeleton pulse.
 *   - Fresh (not submitted): prompt + big "Enter" CTA + participation count.
 *   - Done (submitted today): success chip + streak flame + "View world" CTA.
 */
function WorldQuestCard() {
  const [quest, setQuest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get('/quest/today')
      .then(r => { if (alive) setQuest(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="h-28 rounded-3xl bg-white/[0.04] border border-white/8 animate-pulse" />;
  }
  if (!quest) return null;

  const streakCur = quest.streak?.current || 0;
  const hotStreak = streakCur >= 3;

  return (
    <Link
      to="/app/quest"
      className="block group"
      data-testid="home-world-quest-card"
    >
      <div
        className="relative overflow-hidden rounded-3xl border border-white/12 p-5 sm:p-6 transition hover:border-cyan-400/40"
        style={{
          background:
            'radial-gradient(circle at 15% 0%, rgba(123,47,247,0.4), transparent 55%),' +
            'radial-gradient(circle at 95% 100%, rgba(0,198,255,0.35), transparent 55%),' +
            'linear-gradient(135deg, #12122A 0%, #0A0A1A 100%)',
        }}
      >
        {/* Floating orbs */}
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full blur-3xl opacity-40" style={{ background: '#8B5CF6' }} />
        <div className="absolute -bottom-10 -left-6 w-32 h-32 rounded-full blur-3xl opacity-30" style={{ background: '#22D3EE' }} />

        <div className="relative flex items-center gap-4">
          <div className="text-5xl sm:text-6xl shrink-0 drop-shadow-[0_0_20px_rgba(139,92,246,0.6)]">
            {quest.emoji}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-cyan-300 flex items-center gap-1">
              <Globe2 size={11} /> Today’s World Quest
              {quest.submitted && (
                <span className="ml-1 text-emerald-300 normal-case tracking-normal">· posted ✓</span>
              )}
            </div>
            <div className="mt-1 font-display font-bold text-lg sm:text-xl leading-tight line-clamp-2">
              {quest.prompt}
            </div>
            <div className="mt-1.5 flex items-center gap-3 text-[11px] text-white/60">
              <span>{quest.total_responses.toLocaleString()} answered</span>
              <span
                className={`inline-flex items-center gap-1 ${hotStreak ? 'text-orange-300' : 'text-white/60'}`}
                title="Consecutive days completing the World Quest"
              >
                {'🔥'} {streakCur}-day streak
              </span>
            </div>
          </div>
          <div className="shrink-0 hidden sm:block">
            <div className="px-4 py-2 rounded-full text-xs font-semibold bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white group-hover:brightness-110 transition">
              {quest.submitted ? 'View World →' : 'Join the world feed →'}
            </div>
          </div>
        </div>

        <div className="sm:hidden mt-3">
          <div className="w-full text-center py-2 rounded-full text-xs font-semibold bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white">
            {quest.submitted ? 'View the World Feed' : 'Join the World Feed'}
          </div>
        </div>

        {quest.submitted && (
          <div
            className="relative mt-4 rounded-xl bg-cyan-400/10 border border-cyan-400/25 px-3 py-2 text-[11px] leading-snug text-white/75"
            data-testid="home-quest-streak-nudge"
          >
            Answer tomorrow to keep it alive — every{' '}
            <b className="text-orange-300">7 days</b> unlocks a{' '}
            <b className="text-amber-300">+100★ bonus</b> and a Voyager Streak badge.
          </div>
        )}
      </div>
    </Link>
  );
}
